import { useEffect, useState } from "react";
import { ELK_WORKER_URL } from "../../globals";
import {
  type EdgeRoutingMode,
  type ProcessGraphLayoutResult,
  createElk,
  runLayout,
} from "./processGraphLayout";
import type { ProcessRenderGraph } from "./processGraphModel";

/**
 * Lays `graph` out with ELK. The last result stays available while the next one computes (so a
 * setting change doesn't flash a blank map) and names the inputs it was computed from; `running`
 * is true from the moment the inputs change until their result lands, and `failed` is terminal for
 * an engine that can't run at all (worker script missing, or ELK rejecting).
 */
export function useElkLayout(graph: ProcessRenderGraph, routing: EdgeRoutingMode = "separate") {
  const [layout, setLayout] = useState<
    | (ProcessGraphLayoutResult & { sourceGraph: ProcessRenderGraph; routing: EdgeRoutingMode })
    | null
  >(null);
  const [running, setRunning] = useState(true);
  const [failed, setFailed] = useState(false);
  // One engine (and worker) per layout run rather than per mount: cleanup can then always
  // terminate it, which uniformly covers supersession mid-layout (aborting the wasted
  // computation - its promise never settles), the idle worker after a finished layout, and
  // unmount.
  useEffect(() => {
    let cancelled = false;
    setRunning(true);
    setFailed(false);
    const fail = (error: unknown) => {
      if (cancelled) {
        return;
      }
      console.error("Process graph layout failed", error);
      setRunning(false);
      setFailed(true);
    };
    // The promise (not the resolved engine) is what cleanup holds, so termination is
    // guaranteed even when cleanup runs before creation resolves.
    const engine = createElk(() => fail(new Error(`Failed loading ${ELK_WORKER_URL}`)));
    void engine
      .then(elk => (cancelled ? null : runLayout(graph, elk, routing)))
      .then(result => {
        if (!cancelled && result) {
          setLayout({ ...result, sourceGraph: graph, routing });
          setRunning(false);
        }
      })
      .catch(fail);
    return () => {
      cancelled = true;
      void engine.then(e => e.terminateWorker());
    };
  }, [graph, routing]);
  return { layout, running, failed };
}
